import React, { useEffect, useState } from 'react';
import {
  Text, StyleSheet, View, NativeEventEmitter, AppState, Pressable,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Modal from 'react-native-modal';
import * as Notifications from 'expo-notifications';
import * as Animatable from 'react-native-animatable';
import Moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import Animated, { SlideInRight, SlideOutRight } from 'react-native-reanimated';
import { addNetworkStateListener } from 'expo-network';
import Colors from '../constants/Colors';
import BellIcon from './BellIcon';
import NotificationsCard from './NotificationsCard';
import getTickets from '../requests/Tickets/GetTickets';
import BuildingSelector from './BuildingSelector';
import Style from '../constants/Style';
import DropdownViewPointer from './12pxIcons/DropdownViewPointer';
import WarningIcon from './60pxIcons/WarningIcon';
import QuestionIcon from './24pxIcons/QuestionIcon';
import isMobile from './IsMobile';
import { updateBadgeCount } from '../redux/selectedBuildingSlice';
import { logEvent } from './LogEvent';
import refreshToken from './RefreshToken';
import DeleteIcon16 from './16pxIcons/DeleteIcon16';
import { AppScreen } from '../navigation/LinkingConfiguration';
import GetUnreadNotificationsCount from '../requests/Notifications/GetUnreadNotificationsCount';

interface Props {
  collapsed: boolean;
}

const CustomHeader = (props: Props) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const notificationsCount: number = useSelector(
    (state: { selectedBuilding: { badgeCount: number } }) => state.selectedBuilding.badgeCount,
  ) ?? 0;
  const { name: buildingName }: { id: string, name: string } = useSelector(
    (state: { selectedBuilding: { id: string, name: string } }) => state.selectedBuilding,
  );
  const [ticketsCount, setTicketsCount] = useState<number>(null);
  const [counter, setCounter] = useState<number>(0);
  const [initialLoaded, setInitialLoaded] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [haveInternet, setHaveInternet] = useState<boolean>(true);
  const [haveNewNotification, setHaveNewNotification] = useState<boolean>(false);
  const buildingId = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const navigation = useNavigation();
  const dispatch = useDispatch();

  useEffect(() => {
    AsyncStorage.getItem('email').then(setEmail);
  }, []);

  useEffect(() => {
    const notificationEmitter = new NativeEventEmitter();
    notificationEmitter.addListener('event.updatenotifications', updateNotificationCount);
  }, [email]);

  useEffect(() => {
    if (haveNewNotification) {
      const timer = setTimeout(() => {
        setHaveNewNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return () => { };
  }, [haveNewNotification]);

  const updateNotificationCount = () => {
    GetUnreadNotificationsCount().then((res) => {
      if (!res) return;
      const badgeCount: number = res.count ?? 0;
      if (badgeCount > notificationsCount) setHaveNewNotification(true);
      dispatch(updateBadgeCount(badgeCount));
      Notifications.requestPermissionsAsync({
        ios: {
          allowAlert: true,
          allowBadge: true,
          allowSound: true,
        },
      }).then((value: Notifications.PermissionResponse) => {
        if (value.granted) Notifications.setBadgeCountAsync(badgeCount);
      });
    });
  };

  useEffect(updateNotificationCount, [email, buildingId, counter]);

  useEffect(() => {
    const subscription = Notifications
      .addNotificationReceivedListener(updateNotificationCount) as any;
    return () => subscription.remove();
  }, [email]);

  useEffect(() => {
    refreshToken().then(() => {
      if (AppState.currentState === 'active') {
        if (buildingId !== null) {
          const oldCount = ticketsCount;
          getTickets({ buildingId, limit: 10 }).then((response) => {
            if (response) {
              const newCount = response?.statistics?.statusCounts?.reported ?? 0;
              setTicketsCount(newCount);
              if (initialLoaded && newCount > oldCount) {
                const lastTicketTime = Moment(response.tickets[0].createdOn);
                const notificationEmitter = new NativeEventEmitter();
                notificationEmitter.emit('event.newupdates', { timestamp: lastTicketTime });
              }
              setInitialLoaded(true);
            }
          });
        }
      }
    });
    const interval = setInterval(() => {
      if (AppState.currentState === 'active') {
        setCounter(counter + 1);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [counter]);

  useEffect(() => {
    const subscription = addNetworkStateListener(({ isInternetReachable }) => {
      setHaveInternet(isInternetReachable);
    });

    return () => {
      subscription.remove();
    };
  }, []);

  const openKnowledgeCenter: () => void = () => {
    navigation.navigate(AppScreen.KNOWLEDGE_CENTER as any);
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#fff', paddingLeft: props.collapsed ? 172 : 0 }}>
      <View style={styles.containerStyle}>
        {!isMobile && <Pressable
          style={(state: { pressed: boolean, hovered: boolean }) => [
            styles.knowledgeCenterButton,
            state.hovered && Style.hovered, state.pressed && Style.pressed,
          ]}
          testID='kc-button'
          onPress={openKnowledgeCenter}>
          <QuestionIcon color={styles.knowledgeCenterText.color} />
          <Text style={styles.knowledgeCenterText}>{'Knowledge center'}</Text>
        </Pressable>}
        <View style={styles.contentRight}>
          <Pressable
            style={(state) => [styles.notificationMenu, { opacity: state.hovered ? 0.5 : 1 }]}
            onPress={() => {
              logEvent('Header', 'notification_bell');
              updateNotificationCount();
              setModalVisible(!modalVisible);
            }}
            testID='notifications-btn'
          >
            <BellIcon color={Colors.light.hereworksDarkBlue} />
            {notificationsCount > 0 && (
              <View
                style={[
                  styles.notificationCount,
                  notificationsCount > 99 ? { width: 26, right: -10 } : {},
                ]}
              >
                <Text style={styles.notificationCountText}>{notificationsCount}</Text>
              </View>
            )}
          </Pressable>
          <BuildingSelector
            testID={buildingName?.toLowerCase().includes('qa test building')
              ? 'qa-test-building-selector' : 'header-building-selector'}
          />
        </View>
      </View>
      <Modal
        isVisible={modalVisible}
        onBackdropPress={() => setModalVisible(false)}
        animationIn={'fadeIn'}
        animationOut={'fadeOut'}
        backdropOpacity={0}
        style={Style.modal}
      >
        <View style={styles.notificationDropdownWrapper}>
          <DropdownViewPointer style={styles.pointer} />
          <NotificationsCard hideModal={() => setModalVisible(false)} />
        </View>
      </Modal>
      {!haveInternet && <Animatable.View
        animation={'slideInRight'} style={styles.offlineBanner}
      >
        <WarningIcon color={Colors.light.hereworksBlue500} />
        <Text style={styles.offlineText}>{'You appear to be offline'}</Text>
      </Animatable.View>}
      {haveNewNotification && <Animated.View
        entering={SlideInRight}
        exiting={SlideOutRight}
        style={styles.notificationBanner}
        testID={'new-notification-banner'}>
        <Text style={styles.notificationBannerText}>
          You've received 1 new notification</Text>
        <Pressable
          onPress={() => setHaveNewNotification(false)}
          style={{ marginLeft: 104 }}
          testID='close-notification-banner'>
          <DeleteIcon16 color={Colors.light.hereworksDarkBlue300} />
        </Pressable>
      </Animated.View>}
    </View>
  );
};

const styles = StyleSheet.create({
  containerStyle: {
    backgroundColor: '#FFFFFF',
    height: 92,
    borderLeftWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: 40,
    shadowColor: '#000',
    shadowRadius: 20,
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 8 },
    elevation: 5,
  },
  contentRight: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  notificationMenu: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationCount: {
    position: 'absolute',
    width: 20,
    height: 20,
    top: -2.5,
    right: -3.5,
    borderRadius: 10,
    justifyContent: 'center',
    backgroundColor: Colors.light.hereworksUrgentRed,
  },
  notificationCountText: {
    textAlign: 'center',
    fontSize: 12,
    color: '#fff',
    fontFamily: 'Roboto_700Bold',
  },
  notificationDropdownWrapper: {
    flex: 1,
    justifyContent: 'flex-start',
    alignSelf: 'flex-end',
    alignItems: 'flex-start',
    marginTop: 75,
  },
  pointer: {
    marginStart: 30,
  },
  offlineBanner: {
    backgroundColor: '#fff',
    position: 'absolute',
    end: 20,
    bottom: -100,
    padding: 34,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
  },
  notificationBanner: {
    backgroundColor: Colors.light.hereworksWhite500,
    position: 'absolute',
    flexDirection: 'row',
    end: 40,
    bottom: -77,
    padding: 16,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  offlineText: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Poppins_500Medium',
    marginTop: 14,
  },
  notificationBannerText: {
    color: Colors.light.hereworksBlack600,
    fontSize: 16,
    fontFamily: 'Roboto_400Regular',
  },
  knowledgeCenterButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite100,
    backgroundColor: Colors.light.hereworksWhite500,
    paddingHorizontal: 12,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  knowledgeCenterText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlue400,
    marginStart: 8,
  },
});

export default CustomHeader;
